import * as React from "react";
import { FieldProps } from "@rjsf/utils";
import { useAppDispatch, useAppSelector } from "../../hooks";
import { actions } from "./proofOfWorkSlice";
import { ProofOfWorkVerificationState } from "./ProofOfWorkVerificationState";
import { useServices } from "../../services/ServiceProvider";
import "altcha";

// Extend JSX intrinsic elements to accept the altcha-widget custom element
declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    /** Augments the intrinsic element map to include the altcha-widget custom element. */
    interface IntrinsicElements {
      /** The altcha-widget custom element. */
      // eslint-disable-next-line @typescript-eslint/naming-convention
      "altcha-widget": React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          /** URL (or inline JSON) for the PoW challenge; maps to the widget's `challenge` prop. */
          challenge?: string;
          /** JSON configuration blob passed to the widget's `configure()` API (e.g. `{"verifyUrl":"…"}`). */
          configuration?: string;
          /** Solving mode; "onload" starts automatically without user interaction. */
          auto?: string;
        },
        HTMLElement
      >;
    }
  }
}

/**
 * The value written to the RJSF field once the proof-of-work challenge is solved.
 * The JSON schema marks the proofOfWork field as required, so this value being set
 * enables form submission; undefined/empty keeps the submit button disabled.
 */
const solvedValue = "solved";

/**
 * An RJSF field widget that wraps the altcha-widget custom element.
 * The widget solves the proof-of-work challenge silently on mount.
 * When the server-issued proof cookie is already valid the challenge is skipped.
 *
 * @param props - the RJSF field props
 * @returns the widget markup, or null when the proof cookie is already valid
 */
export const ProofOfWorkWidget: React.FC<FieldProps<string>> = (props) => {
  const dispatch = useAppDispatch();
  const { verificationState } = useAppSelector((state) => state.proofOfWork);
  const { proofOfWorkService } = useServices();
  const widgetRef = React.useRef<HTMLElement>(null);
  // Tracks whether this instance solved the challenge (vs. cookie was already valid on mount)
  const solvedByWidget = React.useRef(false);

  const { challengeUrl, solveUrl } = proofOfWorkService;

  // On mount, override altcha's English i18n strings with our localized versions.
  // ClientStrings is a server-rendered global unavailable at module-evaluation time,
  // so this must run inside an effect rather than at module scope.
  React.useEffect(() => {
    /* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */
    const altchaGlobal = (globalThis as any).$altcha;
    if (altchaGlobal) {
      altchaGlobal.i18n.set("en", {
        ...altchaGlobal.i18n.get("en"),
        label: String(ClientStrings.ProofOfWork_Label),
        error: String(ClientStrings.ProofOfWork_SecurityCheckFailed),
        verified: String(ClientStrings.ProofOfWork_SecurityCheckPassed),
      });
    }
    /* eslint-enable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any */
  }, []);

  // On mount, check whether there is already a valid proof cookie so the
  // widget can be skipped without solving a new challenge.
  React.useEffect(() => {
    void dispatch(actions.checkExistingCookie());
  }, []);

  // If the cookie check already verified the user, signal the form immediately.
  React.useEffect(() => {
    if (verificationState === ProofOfWorkVerificationState.verified) {
      props.onChange(solvedValue);
    }
  }, [verificationState]);

  // When the server rejects the proof (hipValidationError), the slice resets to idle.
  // Clear the form field so the required validator blocks re-submission until re-solved.
  React.useEffect(() => {
    if (verificationState === ProofOfWorkVerificationState.idle && solvedByWidget.current) {
      solvedByWidget.current = false;
      props.onChange(undefined);
    }
  }, [verificationState]);

  // Attach the statechange listener via a ref so the custom event is picked up
  // (JSX synthetic events do not fire for custom element events).
  React.useEffect(() => {
    const el = widgetRef.current;
    if (!el) {
      return;
    }

    /* eslint-disable jsdoc/require-jsdoc */
    const handleStateChange = (evt: Event) => {
      const detail = (evt as CustomEvent<{ state: string }>).detail;
      dispatch(actions.setWidgetState(detail.state));

      if ((detail.state as ProofOfWorkVerificationState) === ProofOfWorkVerificationState.verified) {
        solvedByWidget.current = true;
        props.onChange(solvedValue);
      }
    };
    /* eslint-enable jsdoc/require-jsdoc */

    el.addEventListener("statechange", handleStateChange);
    return () => el.removeEventListener("statechange", handleStateChange);
  }, [widgetRef.current]);

  // Cookie was already valid on mount — hide the widget entirely (silent flow).
  if (verificationState === ProofOfWorkVerificationState.verified && !solvedByWidget.current) {
    return null;
  }

  return (
    <altcha-widget
      ref={widgetRef}
      challenge={challengeUrl}
      configuration={JSON.stringify({ verifyUrl: solveUrl, hideFooter: true, hideLogo: true })}
    />
  );
};
